import React, { useState } from 'react';
import { Button, Col, Input, List, notification, Popconfirm, Row, Radio, RadioChangeEvent, InputNumber, Checkbox } from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined, StopOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { SubTenantInterface } from '@app/modules/Tenants/Tenant.interfaces';
import { Can } from '@app/modules/Auth';
import { useStores } from '@app/hooks/stores';
import api from '../api';
import ProfilesSelect from '@app/modules/Tenants/components/ProfilesSelect';
import CronProfilesSelect from '@app/modules/Tenants/components/CronSelect';
import './Tenants.less';
import { SETPOINT_MODES } from '@app/constants';
import { encodePolicyToSchedule, decodePolicyFromSchedule } from '../schedulePolicyEncoder';

declare type SubTenantItemProps = {
  item: SubTenantInterface;
};

const SubTenantItem: React.FC<SubTenantItemProps> = ({ item }) => {
  const { tenantsStore } = useStores();

  const { saveBackupSubTenant, restoreBackedUpSubTenant, removeSubTenant, selectedTenant, subTenantsMap } =
    tenantsStore;

  const [editingItemsMap, setEditingMode] = useState<{ [key: number]: boolean }>({});
  const [deleteConfirmationsMap, setDeleteConfirmationVisibility] = useState<{ [key: number]: boolean }>({});

  const toggleEditingOnItem = (item, forcedValue) => {
    setEditingMode({
      ...editingItemsMap,
      ...{
        [item.id]: typeof forcedValue !== 'undefined' ? forcedValue : !editingItemsMap[item.id]
      }
    });
  };

  const onEditMode = (item: SubTenantInterface) => {
    saveBackupSubTenant(item);
    toggleEditingOnItem(item, true);
  };

  const onCancelEditing = (item: SubTenantInterface) => {
    if (item.id) {
      restoreBackedUpSubTenant(item);
    } else {
      // remove blank item
      removeSubTenant(item);
    }
    toggleEditingOnItem(item, false);
  };

  const onChangeName = event => {
    const name = event.target.value;
    item.name = name;
  };

  const onSetProfile = defProf => {
    item.defProf = defProf;
  };

  const onSetCronProfile = cronId => {
    item.cronProfileId = cronId;
  };

  const onSave = async (item: SubTenantInterface) => {
    if (!item.name) {
      return notification.error({
        message: 'SubTenant name should not be empty!'
      });
    }

    const alreadyExists = checkSubTenantDuplicates(item.name);
    if (alreadyExists)
      return notification.warning({ message: 'SubTenant with such name already exists in current tenant' });

    // Encode the UI fields into the 32-bit schedulePolicy before sending
    const encodedItem = { ...item };
    encodedItem.schedulePolicy = encodePolicyToSchedule({
      setpointOnMode: item.setpointOnMode,
      setpointOffMode: item.setpointOffMode,
      hardModeOn: item.hardModeOn,
      hardModeOff: item.hardModeOff,
      motionSensorOn: item.motionSensorOn,
      motionSensorOff: item.motionSensorOff,
      longNoMotionOn: item.longNoMotionOn,
      longNoMotionOff: item.longNoMotionOff,
      occupancyTriggeredActivationOn: item.occupancyTriggeredActivationOn,
      occupancyTriggeredActivationOff: item.occupancyTriggeredActivationOff,
      softOperationOn: item.softOperationOn,
      softOperationOff: item.softOperationOff,
      enforceMinimalSetpoint: item.enforceMinimalSetpoint,
      enforceLocalDashboardOffline: item.enforceLocalDashboardOffline
    });

    if (item.id) {
      try {
        const { id, ...body } = encodedItem;
        await api.editSubTenant(id, body);
        // Update item's schedulePolicy with encoded value
        item.schedulePolicy = encodedItem.schedulePolicy;
        notification.success({
          message: 'SubTenant edited successfully!'
        });
      } catch (err) {
        notification.error({
          message: 'An error occurred during SubTenant editing'
        });
      }
    } else {
      try {
        const response = await api.addSubTenant(encodedItem);
        notification.success({
          message: 'New SubTenant created successfully!'
        });
        // Decode the response if it contains schedulePolicy
        if (response.schedulePolicy !== undefined) {
          const decodedPolicy = decodePolicyFromSchedule(response.schedulePolicy);
          Object.assign(item, response, decodedPolicy);
        } else {
          Object.assign(item, response);
        }
      } catch (err) {
        notification.error({
          message: 'An error occurred during SubTenant creating'
        });
      }
    }

    toggleEditingOnItem(item, false);
  };

  const handleDeleteConfirmationVisibility = (visible, item: SubTenantInterface) => {
    setDeleteConfirmationVisibility({
      ...deleteConfirmationsMap,
      ...{
        [item.id]: visible
      }
    });
  };

  const onDelete = async (item: SubTenantInterface) => {
    try {
      await api.deleteSubTenant(item.id);
      removeSubTenant(item);
      notification.success({
        message: `SubTenant "${item.name}" deleted !`
      });
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Error while deleting SubTenant:\n ${e.message}`
      });
    }
  };

  const checkSubTenantDuplicates = (name: string) => {
    const currentSubtenants = subTenantsMap[selectedTenant!.id];
    if (!currentSubtenants) return notification.warning({ message: 'Please, select tenant' });

    const duplicates = currentSubtenants.filter(subtenant => subtenant.name === name);
    return duplicates.length > 1;
  };

  // new checkbox handlers
  const onEnforceMinimalSetpointChange = e => {
    item.enforceMinimalSetpoint = e.target.checked;
  };
  const onSoftOperationOnChange = e => {
    item.softOperationOn = e.target.checked;
  };
  const onSoftOperationOffChange = e => {
    item.softOperationOff = e.target.checked;
  };
  const onEnforceLocalDashboardOfflineChange = e => {
    item.enforceLocalDashboardOffline = e.target.checked;
  };

  // new operation policy table handlers
  const onSetpointOnModeChange = ({ target: { value } }: RadioChangeEvent) => {
    item.setpointOnMode = value;
  };
  const onSetpointOffModeChange = ({ target: { value } }: RadioChangeEvent) => {
    item.setpointOffMode = value;
  };
  const onHardModeOnChange = e => {
    item.hardModeOn = e.target.checked;
  };
  const onHardModeOffChange = e => {
    item.hardModeOff = e.target.checked;
  };
  const onMotionSensorOnChange = e => {
    item.motionSensorOn = e.target.checked;
  };
  const onMotionSensorOffChange = e => {
    item.motionSensorOff = e.target.checked;
  };
  const onLongNoMotionOnChange = e => {
    item.longNoMotionOn = e.target.checked;
  };
  const onLongNoMotionOffChange = e => {
    item.longNoMotionOff = e.target.checked;
  };
  const onOccupancyTriggeredActivationOnChange = e => {
    item.occupancyTriggeredActivationOn = e.target.checked;
  };
  const onOccupancyTriggeredActivationOffChange = e => {
    item.occupancyTriggeredActivationOff = e.target.checked;
  };

  const onMotionThresholdChange = value => {
    item.motionThreshold = value;
  };

  const onMotionHoldingChange = value => {
    item.motionHolding = value;
  };

  const onMotionAutostopChange = value => {
    item.motionAutostop = value;
  };


  const isEditing = !item.id || editingItemsMap[item.id];

  return (
    <List.Item
      className="subtenants-item"
      style={{ cursor: 'pointer' }}
      key={item.id}
      actions={
        isEditing
          ? [
              <Can
                action="put:subtenants"
                yes={() => <Button type="link" icon={<CheckOutlined />} onClick={e => onSave(item)} />}
                no={() => <Button type="link" icon={<CheckOutlined />} disabled />}
              />,
              <Button type="link" icon={<StopOutlined />} onClick={e => onCancelEditing(item)} />
            ]
          : [
              <Can
                action="put:subtenants"
                yes={() => <Button type="link" icon={<EditOutlined />} onClick={e => onEditMode(item)} />}
                no={() => <Button type="link" icon={<EditOutlined />} />}
              />,
              <Can
                action="delete:subtenants"
                yes={() => (
                  <Popconfirm
                    title={`Are you sure you want to delete "${item.name}" sub-tenant?`}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    open={deleteConfirmationsMap[item.id]}
                    onOpenChange={visible => handleDeleteConfirmationVisibility(visible, item)}
                    onConfirm={() => onDelete(item)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
                no={() => <Button type="link" disabled icon={<DeleteOutlined />} />}
              />
            ]
      }
    >
      {isEditing ? (
        <List.Item.Meta
          title={
            <Input
              value={item.name}
              size="middle"
              onChange={value => {
                onChangeName(value);
              }}
              autoFocus
              placeholder="Name"
            />
          }
        />
      ) : (
        <List.Item.Meta title={item.name} />
      )}
      <Row gutter={[24, 16]}>
        <Col lg={8} md={8} sm={24} xs={24}>
          <label>Presence off schedule: </label>
        </Col>
        <Col lg={16} md={16} sm={24} xs={24}>
          <CronProfilesSelect
            onChange={onSetCronProfile}
            disabled={!isEditing}
            selectedValue={String(item.cronName || '')}
            placeholder="Schedule not set"
          />
        </Col>
        <>
          <Col lg={8} md={8} sm={24} xs={24}>
            <label>Apply profile for all rooms: </label>
          </Col>
          <Col lg={16} md={16} sm={24} xs={24}>
            <ProfilesSelect
              selectedValue={item.defProf}
              onChange={onSetProfile}
              disabled={!isEditing}
              placeholder="Profile not selected"
            />
          </Col>
        </>
          <Col lg={8} md={8} sm={24} xs={24}>
            <label>Operations policy: </label>
          </Col>
        {/* legacy radio group removed per design update */}
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={24}>
          <div className="operation-policy-table">
            <table>
              <thead>
                <tr>
                  <th>Time slots</th>
                  <th>Presence ON</th>
                  <th>Presence OFF</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Setpoint</td>
                  <td>
                    <Radio.Group
                      disabled={!isEditing}
                      onChange={onSetpointOnModeChange}
                      value={item.setpointOnMode ?? 0}
                    >
                      {SETPOINT_MODES.map((label, i) => (
                        <Radio key={i} value={i}>
                          {label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </td>
                  <td>
                    <Radio.Group
                      disabled={!isEditing}
                      onChange={onSetpointOffModeChange}
                      value={item.setpointOffMode ?? 0}
                    >
                      {SETPOINT_MODES.map((label, i) => (
                        <Radio key={i} value={i}>
                          {label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </td>
                </tr>
                <tr>
                  <td>locked operation mode</td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.hardModeOn}
                      onChange={onHardModeOnChange}
                    >
                      Operation ON
                    </Checkbox>
                  </td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.hardModeOff}
                      onChange={onHardModeOffChange}
                    >
                      Operation OFF
                    </Checkbox>
                  </td>
                </tr>
                <tr>
                  <td>Occupancy-driven cooling</td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.motionSensorOn}
                      onChange={onMotionSensorOnChange}
                    >
                      active
                    </Checkbox>
                  </td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.motionSensorOff}
                      onChange={onMotionSensorOffChange}
                    >
                      active
                    </Checkbox>
                  </td>
                </tr>
                <tr>
                  <td>extended inactivity trigger</td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.longNoMotionOn}
                      onChange={onLongNoMotionOnChange}
                    >
                      active
                    </Checkbox>
                  </td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.longNoMotionOff}
                      onChange={onLongNoMotionOffChange}
                    >
                      active
                    </Checkbox>
                  </td>
                </tr>
                <tr>
                  <td>Occupancy-triggered activation</td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.occupancyTriggeredActivationOn}
                      onChange={onOccupancyTriggeredActivationOnChange}
                    >
                      active
                    </Checkbox>
                  </td>
                  <td>
                    <Checkbox
                      disabled={!isEditing}
                      checked={!!item.occupancyTriggeredActivationOff}
                      onChange={onOccupancyTriggeredActivationOffChange}
                    >
                      active
                    </Checkbox>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>

      {/* extra toggles section */}
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <Checkbox
            disabled={!isEditing}
            checked={!!item.enforceMinimalSetpoint}
            onChange={onEnforceMinimalSetpointChange}
          >
            setpoint floor
          </Checkbox>
        </Col>
      </Row>
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <Checkbox
            disabled={!isEditing}
            checked={!!item.softOperationOn}
            onChange={onSoftOperationOnChange}
          >
            default active operation trigger on presence ON
          </Checkbox>
        </Col>
      </Row>
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <Checkbox
            disabled={!isEditing}
            checked={!!item.softOperationOff}
            onChange={onSoftOperationOffChange}
          >
            default inactive operation trigger on presence OFF
          </Checkbox>
        </Col>
      </Row>
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <Checkbox
            disabled={!isEditing}
            checked={!!item.enforceLocalDashboardOffline}
            onChange={onEnforceLocalDashboardOfflineChange}
          >
            enforce network resiliency mode
          </Checkbox>
        </Col>
      </Row>

      <Row gutter={[24, 16]} className="motion-sensor-title">
        <Col lg={8} md={8} sm={24} xs={24}>
          <List.Item.Meta title="Occupancy Sensor" />
        </Col>
      </Row>
      <Row gutter={[24, 16]} className="motion-sensor-data">
        <>
          <Col lg={8} md={8} sm={24} xs={24}>
            <label>Detection sensitivity: </label>
          </Col>
          <Col lg={16} md={16} sm={24} xs={24} className="with-label">
            <InputNumber
              disabled={!isEditing}
              onChange={onMotionThresholdChange}
              min={1000}
              max={20000}
              value={item.motionThreshold}
            />
            <div className="label">Please input value from 1000 ms to 20000 ms</div>
          </Col>
        </>

        <>
          <Col lg={8} md={8} sm={24} xs={24}>
            <div>Higher temperature after: </div>
          </Col>
          <Col lg={16} md={16} sm={24} xs={24}>
            <InputNumber
              min={3}
              max={480}
              disabled={!isEditing}
              onChange={onMotionHoldingChange}
              addonAfter="min"
              value={item.motionHolding}
            />
          </Col>
        </>

        <>
          <Col lg={8} md={8} sm={24} xs={24}>
            <label>Switch off after: </label>
          </Col>
          <Col lg={16} md={16} sm={24} xs={24} className="with-label">
            <InputNumber
              max={480}
              disabled={!isEditing}
              onChange={onMotionAutostopChange}
              addonAfter="min"
              value={item.motionAutostop}
            />
            <div className="label">0 to disable auto-switch off</div>
          </Col>
        </>
      </Row>
    </List.Item>
  );
};

export default observer(SubTenantItem);
